/**
 * One source of truth for the site's section structure.
 *
 * Used by:
 * - `SiteNav` (mega-menu top bar)
 * - `SiblingNav` (right column / mobile bottom block)
 * - Future sitemap page
 *
 * Each top-level entry is a **section** — a content grouping with sibling
 * pages. A section may have zero pages (renders as a flat top-nav link).
 */

export type SectionPage = {
	to: string
	label: string
	description?: string
}

export type Section = {
	/** The section hub URL — also the destination when clicking the top-nav label. */
	to: string
	label: string
	/** Pages inside the section. Empty → top item is a flat link. */
	pages: SectionPage[]
	/** Extra path prefixes that belong to this section for active matching
	 *  (e.g. `/rata/$slug` single course pages belong to the Radat section). */
	matchPaths?: string[]
}

export const SECTIONS: Section[] = [
	{
		to: '/radat',
		label: 'Radat',
		// Single-course pages (/rata/$slug), legacy preset URLs
		// (/radat/parhaat etc.), city/region URLs, and related info pages
		// all belong to the Radat section.
		matchPaths: [
			'/rata',
			'/luokitukset',
			'/live',
			'/frisbeegolfradan-rakentaminen',
			'/rataopasteet',
		],
		pages: [
			{
				to: '/radat',
				label: 'Kaikki radat',
				description: 'Selaa, suodata, lajittele — listana tai kartalla',
			},
			{
				to: '/luokitukset',
				label: 'Luokitukset',
				description: 'A–D-luokat, tasot ja FAQ',
			},
			{
				to: '/live',
				label: 'Live-kävijäseuranta',
				description: 'Reaaliaikainen kävijäseuranta ratoja kohden',
			},
			{
				to: '/frisbeegolfradan-rakentaminen',
				label: 'Frisbeegolfradan rakentaminen',
				description: 'Vinkit ja ohjeet uuden radan suunnitteluun',
			},
			{
				to: '/rataopasteet',
				label: 'Rataopasteet',
				description: 'Parkdly-väyläopasteet ratojen hallintaan',
			},
		],
	},
	{
		to: '/frisbeegolfradat-kartalla',
		label: 'Kartta',
		pages: [],
	},
	{
		to: '/frisbeegolf',
		label: 'Frisbeegolf',
		pages: [
			{
				to: '/frisbeegolf',
				label: 'Yleistä',
				description: 'Mitä frisbeegolf on ja miten siihen pääsee alkuun',
			},
			{
				to: '/frisbeegolf/saannot',
				label: 'Säännöt',
				description: 'Säännöt ja kentällä noudatettava etiketti',
			},
			{
				to: '/frisbeegolf/heittotekniikat',
				label: 'Heittotekniikat',
				description: 'Backhand, forehand ja erilaiset otteet',
			},
			{
				to: '/frisbeegolf/kiekonvalinta',
				label: 'Kiekonvalinta',
				description: 'Oikean kiekon valinta omaan tasoon',
			},
			{
				to: '/frisbeegolf/valineet',
				label: 'Välineet',
				description: 'Mistä hankit kiekot ja muut varusteet',
			},
			{
				to: '/frisbeegolf/mobiilisovellukset',
				label: 'Mobiilisovellukset',
				description: 'Tuloskortit, kartat ja muut sovellukset',
			},
		],
	},
	{
		to: '/uutiset',
		label: 'Yhteisö',
		// /uutiset is the section's landing page; other community URLs are
		// siblings underneath.
		matchPaths: ['/tapahtumat', '/seurat'],
		pages: [
			{
				to: '/uutiset',
				label: 'Uutiset',
				description: 'Lajin tuoreimmat uutiset',
			},
			{
				to: '/tapahtumat',
				label: 'Tapahtumat',
				description: 'Tulevat kilpailut ja tapahtumat',
			},
			{
				to: '/seurat',
				label: 'Seurat',
				description: 'Suomen frisbeegolfseurat',
			},
		],
	},
]

/* ────────────────────────────────────────────────────────────────────────── */
/* Lookups                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

function pathMatches(pathname: string, target: string): boolean {
	if (target === '/') return pathname === '/'
	return pathname === target || pathname.startsWith(target + '/')
}

/** Find the section a given pathname belongs to, or undefined. */
export function getSectionForPath(pathname: string): Section | undefined {
	return SECTIONS.find((s) => {
		if (pathMatches(pathname, s.to)) return true
		if (s.matchPaths?.some((p) => pathMatches(pathname, p))) return true
		return s.pages.some((p) => pathMatches(pathname, p.to))
	})
}

export function isSectionActive(pathname: string, section: Section): boolean {
	return getSectionForPath(pathname) === section
}

export function isPageActive(pathname: string, page: SectionPage): boolean {
	return pathname === page.to
}
